import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface BusinessIncome {
  id: string;
  property_id?: string | null;
  income_type:
    | "Hospedaje"
    | "Limpieza"
    | "Lavandería"
    | "Comisión"
    | "Administración"
    | "Extras"
    | "Extraordinario";
  payment_type: "Efectivo" | "Transferencia" | "Deducción";
  gross_payment?: number;
  iva?: number;
  total_payment: number;
  payment_status: "Pendiente" | "Pagado" | "Cancelado";
  description?: string;
  payment_date?: string;
  created_at: string;
  updated_at: string;
}

export interface BusinessIncomeFilters {
  propertyId?: string;
  incomeType?: string;
  startDate?: string;
  endDate?: string;
}

export const useBusinessIncome = (filters?: BusinessIncomeFilters) => {
  return useQuery({
    queryKey: ["business-income", filters],
    queryFn: async () => {
      let query = supabase
        .from("business_income")
        .select(
          `
          *,
          properties(title, short_name)
        `,
        )
        .order("created_at", { ascending: false });

      if (filters?.propertyId && filters.propertyId !== "all") {
        query = query.eq("property_id", filters.propertyId);
      }

      if (filters?.incomeType && filters.incomeType !== "all") {
        // Normalize both sides for accent-insensitive comparison
        const normalizedFilter = filters.incomeType
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
        
        query = query.ilike("income_type", normalizedFilter);
      }

      if (filters?.startDate) {
        query = query.gte("payment_date", filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte("payment_date", filters.endDate);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching business income:", error);
        throw error;
      }

      return (data || []).map((item) => ({
        ...item,
        income_type: item.income_type as BusinessIncome["income_type"],
        payment_type: item.payment_type as BusinessIncome["payment_type"],
        payment_status: item.payment_status as BusinessIncome["payment_status"],
      }));
    },
  });
};

export const useCreateBusinessIncome = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (
      income: Omit<BusinessIncome, "id" | "created_at" | "updated_at">,
    ) => {
      const { data, error } = await supabase
        .from("business_income")
        .insert([income])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-income"] });
      queryClient.invalidateQueries({ queryKey: ["business-income-paginated"] });
      toast({
        title: "Éxito",
        description: "Ingreso creado correctamente",
      });
    },
    onError: (error) => {
      console.error("Error creating business income:", error);
      toast({
        title: "Error",
        description: "Error al crear el ingreso",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateBusinessIncome = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<BusinessIncome> & { id: string }) => {
      const { data, error } = await supabase
        .from("business_income")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-income"] });
      queryClient.invalidateQueries({ queryKey: ["business-income-paginated"] });
      toast({
        title: "Éxito",
        description: "Ingreso actualizado correctamente",
      });
    },
    onError: (error) => {
      console.error("Error updating business income:", error);
      toast({
        title: "Error",
        description: "Error al actualizar el ingreso",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteBusinessIncome = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("business_income")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-income"] });
      queryClient.invalidateQueries({ queryKey: ["business-income-paginated"] });
      toast({
        title: "Éxito",
        description: "Ingreso eliminado correctamente",
      });
    },
    onError: (error) => {
      console.error("Error deleting business income:", error);
      toast({
        title: "Error",
        description: "Error al eliminar el ingreso",
        variant: "destructive",
      });
    },
  });
};
